import { $Call } from 'utility-types';
import type { ReactNode } from 'react';
import React, { Children } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import type { Props as IconProps } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { BUTTON_SIZES, themes } from './Button.themes';
export type ButtonProps = {
  children?: ReactNode;
  className?: string;
  color?: Colors;
  colorBg?: Colors;
  colorBorder?: Colors;
  iconLeft?: IconProps | null | undefined;
  iconRight?: IconProps | null | undefined;
  isDisabled?: boolean;
  isExternalLink?: boolean;
  isLoading?: boolean;
  isSquare?: boolean;
  onClick?: (arg0: React.SyntheticEvent) => unknown;
  queryParamsToIgnore?: ReadonlyArray<string>;
  size?: keyof typeof BUTTON_SIZES;
  target?: string;
  theme?: keyof $Call<typeof themes>;
  trackingData?: TrackingData;
  type?: 'button' | 'submit' | 'reset';
  url?: string | null | undefined;
};

const ButtonIcon = (props: IconProps | null | undefined) =>
  props?.name ? <Icon hasRtl width="1em" {...props} /> : null;

const getRoleProps = ({ isAnchor, isExternalLink, queryParamsToIgnore, target, type, url }) => {
  if (isExternalLink) {
    return {
      href: url,
      target,
    };
  }

  if (isAnchor) {
    return {
      queryParamsToIgnore,
      target,
      url,
    };
  }

  return {
    type,
  };
};

export const Button = ({
  children,
  className,
  color,
  colorBg,
  colorBorder,
  iconLeft,
  iconRight,
  isDisabled,
  isExternalLink,
  isLoading,
  isSquare,
  onClick,
  queryParamsToIgnore,
  size = 'large',
  target,
  theme = 'default',
  trackingData,
  type = 'button',
  url,
  ...rest
}: ButtonProps) => {
  const track = useTracking(trackingData);

  if (!Children.count(children) || !children) {
    return null;
  }

  const isAnchor = !!url;
  const Comp = isAnchor ? DynamicLink : 'button';
  const TagName = isExternalLink ? 'a' : Comp;
  const { thButton, thIcon, thSpinner } = themes({
    color,
    colorBg,
    colorBorder,
    isDisabled,
    isLoading,
    isSquare,
    size,
  })[theme];
  return (
    <TagName
      {...rest}
      aria-disabled={isDisabled}
      className={classnames(thButton, className)}
      disabled={isDisabled}
      onClick={(e) => {
        if (trackingData) {
          track();
        }

        onClick?.(e);
      }}
      rel={isExternalLink ? 'nofollow' : undefined}
      {...getRoleProps({
        isAnchor,
        isExternalLink,
        queryParamsToIgnore,
        target,
        type,
        url,
      })}
    >
      <ButtonIcon {...iconLeft} className={classnames(thIcon, iconLeft?.className)} />
      {children}
      <ButtonIcon {...iconRight} className={classnames(thIcon, iconRight?.className)} />
      {isLoading && !isDisabled && (
        <Spinner className={classnames('text-current', thSpinner)} size="small" />
      )}
    </TagName>
  );
};
